import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { applyManifestForPath } from "./lib/portalPwa";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";
import { InstallPrompt } from "./components/InstallPrompt";

import Login from "./pages/Login";
import Layout from "./components/Layout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminClients from "./pages/admin/AdminClients";
import AdminResellers from "./pages/admin/AdminResellers";
import AdminReports from "./pages/admin/AdminReports";
import AdminAI from "./pages/admin/AdminAI";
import AdminLimitRequests from "./pages/admin/AdminLimitRequests";
import AdminInsurance from "./pages/admin/AdminInsurance";
import AdminHeatmap from "./pages/admin/AdminHeatmap";
import AdminPix from "./pages/admin/AdminPix";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCompany from "./pages/admin/AdminCompany";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminEletros from "./pages/admin/AdminEletros";
import AdminEletrosCatalog from "./pages/admin/AdminEletrosCatalog";
import AdminEletrosRecebimentos from "./pages/admin/AdminEletrosRecebimentos";
import AdminEletrosSaques from "./pages/admin/AdminEletrosSaques";
import AdminEletrosEstoque from "./pages/admin/AdminEletrosEstoque";
import AdminEletrosRelatorios from "./pages/admin/AdminEletrosRelatorios";
import RevendaEletros from "./pages/reseller/RevendaEletros";
import EletrosBuyers from "./pages/eletros/EletrosBuyers";
import EletrosBuyerProfile from "./pages/eletros/EletrosBuyerProfile";
import LogisticaDashboard from "./pages/admin/LogisticaDashboard";
import LogisticaDeliveries from "./pages/admin/LogisticaDeliveries";
import LogisticaDrivers from "./pages/admin/LogisticaDrivers";
import LogisticaRoutes from "./pages/admin/LogisticaRoutes";
import {
  PortalAccess, PortalRoot, PortalHome, PortalPurchases, PortalPurchaseDetail,
  PortalScore, PortalReceipts, PortalConta,
} from "./pages/portal/Portal";
import LogisticaMapsConfig from "./pages/admin/LogisticaMapsConfig";
import LogisticaPodConfig from "./pages/admin/LogisticaPodConfig";
import LogisticaWhatsAppConfig from "./pages/admin/LogisticaWhatsAppConfig";
import DriverPortal from "./pages/DriverPortal";
import RevendaEletrosSaleWizard from "./pages/reseller/RevendaEletrosSaleWizard";
import EletrosComissoes from "./pages/reseller/EletrosComissoes";
import EletrosContaCorrente from "./pages/reseller/EletrosContaCorrente";
import AdminBillingRuler from "./pages/admin/AdminBillingRuler";
import AutomationsWhatsApp from "./pages/admin/AutomationsWhatsApp";
import PaymentCenterAdmin from "./pages/admin/PaymentCenterAdmin";
import AdminWaPortalGuide from "./pages/admin/AdminWaPortalGuide";
import CreditRadar from "./pages/admin/CreditRadar";
import CreditDashboard from "./pages/admin/CreditDashboard";
import CreditPerformance from "./pages/admin/CreditPerformance";
import CreditAlerts from "./pages/admin/CreditAlerts";
import CRMPage from "./pages/CRMPage";
import ComprovantesPage from "./pages/ComprovantesPage";
import ResellerDashboard from "./pages/reseller/ResellerDashboard";
import ResellerClients from "./pages/reseller/ResellerClients";
import NewClient from "./pages/reseller/NewClient";
import NewSale from "./pages/reseller/NewSale";
import ResellerSettings from "./pages/reseller/ResellerSettings";
import SaleDetail from "./pages/SaleDetail";
import EditSale from "./pages/EditSale";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientInstallments from "./pages/client/ClientInstallments";
import SignPromissory from "./pages/public/SignPromissory";
import SignPromissoryEletros from "./pages/public/SignPromissoryEletros";
import VerifyPromissory from "./pages/public/VerifyPromissory";
import ClientDetail from "./pages/ClientDetail";
import ResellerProfile from "./pages/ResellerProfile";
import MyAffiliate from "./pages/reseller/MyAffiliate";
import AffiliateLanding from "./pages/public/AffiliateLanding";
import RecoveryPage from "./pages/RecoveryPage";
import TransfersPage from "./pages/TransfersPage";
import OpportunitiesPage from "./pages/reseller/OpportunitiesPage";
import OpportunityDetail from "./pages/reseller/OpportunityDetail";
import AdminOpportunities from "./pages/admin/AdminOpportunities";
import AdminOpportunityDetail from "./pages/admin/AdminOpportunityDetail";
import Setup from "./pages/Setup";

function Protected({ children, roles, allowEletrosAdmin }) {
  const { user } = useAuth();
  if (user === undefined) return (
    <div className="min-h-screen flex items-center justify-center text-sm tracking-[0.18em] uppercase text-[#5C5A57]">Carregando</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (allowEletrosAdmin && user.role === "reseller" && user.eletros_admin) return children;
    return <Navigate to="/" replace />;
  }
  return children;
}

function RoleRouter() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "reseller") return <Navigate to="/revenda" replace />;
  if (user.role === "driver") return <Navigate to="/entregas" replace />;
  return <Navigate to="/cliente" replace />;
}

function ManifestSync() {
  const { pathname } = useLocation();
  React.useEffect(() => { applyManifestForPath(pathname); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" expand duration={4000} gap={10} />
        <ManifestSync />
        <InstallPrompt />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/assinar/:saleId" element={<SignPromissory />} />
          <Route path="/eletros/assinar/:saleId" element={<SignPromissoryEletros />} />
          <Route path="/verificar/:promissoryId" element={<VerifyPromissory />} />
          <Route path="/com/:slug" element={<AffiliateLanding />} />
          <Route path="/entregas" element={<Protected roles={["driver"]}><DriverPortal /></Protected>} />

          {/* Portal do Cliente (sessao propria por CPF + nascimento) */}
          <Route path="/cliente/acesso" element={<PortalAccess />} />
          <Route path="/cliente" element={<PortalRoot />}>
            <Route index element={<PortalHome />} />
            <Route path="compras" element={<PortalPurchases />} />
            <Route path="compras/:bu/:id" element={<PortalPurchaseDetail />} />
            <Route path="score" element={<PortalScore />} />
            <Route path="recibos" element={<PortalReceipts />} />
            <Route path="conta" element={<PortalConta />} />
          </Route>


          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/" element={<RoleRouter />} />

            {/* Admin */}
            <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/logistica" element={<Protected roles={["admin"]}><LogisticaDashboard /></Protected>} />
            <Route path="/admin/logistica/entregas" element={<Protected roles={["admin"]}><LogisticaDeliveries /></Protected>} />
            <Route path="/admin/logistica/entregadores" element={<Protected roles={["admin"]}><LogisticaDrivers /></Protected>} />
            <Route path="/admin/logistica/rotas" element={<Protected roles={["admin"]}><LogisticaRoutes /></Protected>} />
            <Route path="/admin/logistica/maps" element={<Protected roles={["admin"]}><LogisticaMapsConfig /></Protected>} />
            <Route path="/admin/logistica/prova" element={<Protected roles={["admin"]}><LogisticaPodConfig /></Protected>} />
            <Route path="/admin/logistica/whatsapp" element={<Protected roles={["admin"]}><LogisticaWhatsAppConfig /></Protected>} />
            <Route path="/admin/clientes" element={<Protected roles={["admin"]}><AdminClients /></Protected>} />
            <Route path="/admin/clientes/:id" element={<Protected roles={["admin","reseller"]}><ClientDetail /></Protected>} />
            <Route path="/admin/revendedoras" element={<Protected roles={["admin"]}><AdminResellers /></Protected>} />
            <Route path="/admin/revendedoras/:id" element={<Protected roles={["admin"]}><ResellerProfile /></Protected>} />
            <Route path="/admin/relatorios" element={<Protected roles={["admin"]}><AdminReports /></Protected>} />
            <Route path="/admin/ia" element={<Protected roles={["admin"]}><AdminAI /></Protected>} />
            <Route path="/admin/limites" element={<Protected roles={["admin"]}><AdminLimitRequests /></Protected>} />
            <Route path="/admin/seguro" element={<Protected roles={["admin"]}><AdminInsurance /></Protected>} />
            <Route path="/admin/mapa" element={<Protected roles={["admin"]}><AdminHeatmap /></Protected>} />
            <Route path="/admin/pix" element={<Protected roles={["admin"]}><AdminPix /></Protected>} />
            <Route path="/admin/crm" element={<Protected roles={["admin"]}><CRMPage /></Protected>} />
            <Route path="/admin/comprovantes" element={<Protected roles={["admin"]}><ComprovantesPage /></Protected>} />
            <Route path="/admin/regua" element={<Protected roles={["admin"]}><AdminBillingRuler /></Protected>} />
            <Route path="/admin/central-pagamentos" element={<Protected roles={["admin"]}><PaymentCenterAdmin /></Protected>} />
            <Route path="/admin/ia-portal" element={<Protected roles={["admin"]}><AdminWaPortalGuide /></Protected>} />
            <Route path="/admin/automacoes" element={<Protected roles={["admin"]}><AutomationsWhatsApp /></Protected>} />
            <Route path="/admin/empresa" element={<Protected roles={["admin"]}><AdminCompany /></Protected>} />
            <Route path="/admin/produtos" element={<Protected roles={["admin"]}><AdminProducts /></Protected>} />
            <Route path="/admin/eletros" element={<Protected roles={["admin"]}><AdminEletros /></Protected>} />
            <Route path="/admin/eletros/catalogo" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminEletrosCatalog /></Protected>} />
            <Route path="/admin/eletros/recebimentos" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminEletrosRecebimentos /></Protected>} />
            <Route path="/admin/eletros/comissoes" element={<Protected roles={["admin"]} allowEletrosAdmin><EletrosComissoes /></Protected>} />
            <Route path="/admin/eletros/saques" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminEletrosSaques /></Protected>} />
            <Route path="/admin/eletros/estoque" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminEletrosEstoque /></Protected>} />
            <Route path="/admin/eletros/relatorios" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminEletrosRelatorios /></Protected>} />
            <Route path="/admin/eletros/compradores" element={<Protected roles={["admin"]} allowEletrosAdmin><EletrosBuyers admin /></Protected>} />
            <Route path="/admin/eletros/compradores/:clientId" element={<Protected roles={["admin"]} allowEletrosAdmin><EletrosBuyerProfile admin basePath="/admin/eletros/compradores" /></Protected>} />
            <Route path="/revenda/eletros" element={<Protected roles={["reseller","admin"]}><RevendaEletros /></Protected>} />
            <Route path="/revenda/eletros/venda" element={<Protected roles={["reseller","admin"]}><RevendaEletrosSaleWizard /></Protected>} />
            <Route path="/revenda/eletros/comissoes" element={<Protected roles={["reseller","admin"]}><EletrosComissoes /></Protected>} />
            <Route path="/revenda/eletros/conta" element={<Protected roles={["reseller","admin"]}><EletrosContaCorrente /></Protected>} />
            <Route path="/revenda/eletros/compradores" element={<Protected roles={["reseller","admin"]}><EletrosBuyers /></Protected>} />
            <Route path="/revenda/eletros/compradores/:clientId" element={<Protected roles={["reseller","admin"]}><EletrosBuyerProfile basePath="/revenda/eletros/compradores" /></Protected>} />
            <Route path="/revenda/eletros/saques" element={<Protected roles={["reseller","admin"]}><EletrosContaCorrente /></Protected>} />
            <Route path="/admin/configuracoes" element={<Protected roles={["admin"]}><AdminSettings /></Protected>} />
            <Route path="/admin/credito" element={<Protected roles={["admin"]}><CreditDashboard /></Protected>} />
            <Route path="/admin/credito/performance" element={<Protected roles={["admin"]}><CreditPerformance /></Protected>} />
            <Route path="/admin/credito/alertas" element={<Protected roles={["admin"]}><CreditAlerts /></Protected>} />
            <Route path="/admin/credito/radar" element={<Protected roles={["admin"]}><CreditRadar /></Protected>} />
            <Route path="/revenda/credito/radar" element={<Protected roles={["reseller","admin"]}><CreditRadar /></Protected>} />
            <Route path="/revenda/crm" element={<Protected roles={["reseller","admin"]}><CRMPage /></Protected>} />
            <Route path="/revenda/comprovantes" element={<Protected roles={["reseller","admin"]}><ComprovantesPage /></Protected>} />
            <Route path="/admin/recuperacao" element={<Protected roles={["admin"]}><RecoveryPage /></Protected>} />
            <Route path="/revenda/recuperacao" element={<Protected roles={["reseller","admin"]}><RecoveryPage /></Protected>} />
            <Route path="/revenda/afiliacao" element={<Protected roles={["reseller","admin"]}><MyAffiliate /></Protected>} />
            <Route path="/revenda/transferencias" element={<Protected roles={["reseller","admin"]}><TransfersPage /></Protected>} />
            <Route path="/admin/transferencias" element={<Protected roles={["admin"]}><TransfersPage /></Protected>} />
            <Route path="/revenda/oportunidades" element={<Protected roles={["reseller","admin"]}><OpportunitiesPage /></Protected>} />
            <Route path="/revenda/oportunidades/:id" element={<Protected roles={["reseller","admin"]}><OpportunityDetail /></Protected>} />
            <Route path="/admin/oportunidades" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminOpportunities /></Protected>} />
            <Route path="/admin/oportunidades/:id" element={<Protected roles={["admin"]} allowEletrosAdmin><AdminOpportunityDetail /></Protected>} />

            {/* Reseller */}
            <Route path="/revenda" element={<Protected roles={["reseller"]}><ResellerDashboard /></Protected>} />
            <Route path="/revenda/clientes" element={<Protected roles={["reseller","admin"]}><ResellerClients /></Protected>} />
            <Route path="/revenda/clientes/novo" element={<Protected roles={["reseller","admin"]}><NewClient /></Protected>} />
            <Route path="/revenda/clientes/:id" element={<Protected roles={["reseller","admin"]}><ClientDetail /></Protected>} />
            <Route path="/revenda/venda/nova" element={<Protected roles={["reseller","admin"]}><NewSale /></Protected>} />
            <Route path="/revenda/configuracoes" element={<Protected roles={["reseller"]}><ResellerSettings /></Protected>} />
            <Route path="/revenda/perfil" element={<Protected roles={["reseller","admin"]}><ResellerProfile /></Protected>} />

            {/* Shared */}
            <Route path="/vendas/:saleId" element={<Protected><SaleDetail /></Protected>} />
            <Route path="/vendas/:saleId/editar" element={<Protected roles={["admin","reseller"]}><EditSale /></Protected>} />

            {/* Client */}
            <Route path="/cliente" element={<Protected roles={["client"]}><ClientDashboard /></Protected>} />
            <Route path="/cliente/parcelas" element={<Protected roles={["client"]}><ClientInstallments /></Protected>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
